<template>
  <div class="user-tasks text-dark">
    <div class="user-tasks__header no-print" v-if="isShowHeader">
      <div class="user-tasks__header__left">
        <h2 class="user-tasks__header__left__inner">
          <h2 v-if="!isCanSeeOtherUsers">{{ user.name }}</h2>
          <SelectUser v-else :preSelectedUserId="user_id" :userName="user_name" path="hours">
          </SelectUser>
          <div class="no-mobile">-</div> <p v-if="isKindergarten">Årsnormsoversigt</p><p v-else>{{ $t('vue2.task_overview.hour_overview') }}</p>
        </h2>
        <ChangeSchoolYear :whiteTheme="true" v-if="!initialStartDate"></ChangeSchoolYear>
      </div>
      <div class="user-tasks__header__right">
        <p class="d-link" @click="resetDates" v-if="!isStartOrEndDateChanged">{{ $t('vue2.task_overview.reset') }}</p>
        <DatePickerRangeShortcuts
          v-if="activeSchoolYear"
          :startDate="activeSchoolYear.employee_time_start"
          :endDate="activeSchoolYear.employee_time_end"
          :selectedStartDate="startDate"
          :selectedEndDate="endDate"
          @shortcutSelected="setDatesFromRange">
        </DatePickerRangeShortcuts>
        <DatePickerRange
          :startdate="startDate"
          :enddate="endDate"
          :maxEndDate="activeSchoolYear ? activeSchoolYear.employee_time_end : null"
          @startchanged="setStartDate"
          @endchanged="setEndDate">
        </DatePickerRange>
        <button v-if="isShowPrintButton" class="no-mobile marg-1-left" @click="print">
          <span class="icon-printer3"></span>
        </button>
      </div>
    </div>
    <div class="d-box marg-1-bottom no-mobile" v-if="isShowScheduled && isShowHeader">
      <UserTasksChart
        :startDate="startDate"
        :endDate="endDate"
        :calendarId="user.user_calendar_id">
      </UserTasksChart>
    </div>
    <Loader v-if="isLoadingTasksForUser"></Loader>
    <div class="marg-1-bottom" v-else>
      <UserTasksOverview
        :user="user"
        :dates="dates"
        :activeUserId="this.user.id"
        :isInUserCalendar="true"
        :isKindergarten="isKindergarten"
        :startDate="startDate"
        :endDate="endDate"
        :isShowHeader="isShowHeader"
        :isPoolsDeactivated="isPoolsDeactivated"
        :schoolPrepFactor="schoolPrepFactor"
        :isPreviewAsEmployee="isPreviewAsEmployee"
        :canEditTasks="canEditTasks"
        @changedIsShowScheduled="setIsShowScheduled"
        @editSubject="setEditSubject">
      </UserTasksOverview>
    </div>
    <SubjectForm
      v-if="isSubjectModal"
      :showModal="isSubjectModal"
      :editSubject="editSubject"
      :propIsLoading="isSubjectLoading"
      :allowDanishPreparationFactorUi="true"
      @deleteSubject="deleteSubject"
      @close="closeSubjectModal"
      @updateSubject="updateSubject"
      @saveAndShowTab="saveAndShowTab">
    </SubjectForm>
  </div>
</template>

<script>
import DatePickerRange from '../datepicker/DatePickerRange'
import UserTasksChart from './UserTasksChart'
import UserTasksOverview from './UserTasksOverview'
import UserTasksTable from './UserTasksTable'
import ChangeSchoolYear from '../School/ChangeSchoolYear'
import Loader from '../common/Loader'
import SelectUser from '../common/SelectUser'
import moment from 'moment'
import DatePickerRangeShortcuts from '../datepicker/DatePickerRangeShortcuts'
import {parseNumericPreparationFactor} from '../../utils/preparationFactor'
import SubjectForm from '../Subjects/SubjectForm'
import eventbus from '../../eventbus'
import docendo from '../../docendo'

export default {
  components: {
    DatePickerRangeShortcuts,
    Loader,
    ChangeSchoolYear,
    UserTasksOverview,
    DatePickerRange,
    UserTasksChart,
    UserTasksTable,
    SelectUser,
    SubjectForm
  },
  props: {
    user_id: {
      default: 13
    },
    user_calendar_id: {
      default: 7
    },
    user_name: {
      default: ''
    },
    isShowHeader: {
      type: Boolean,
      default: true
    },
    isShowPrintButton: {
      type: Boolean,
      default: true
    },
    isCanSeeOtherUsers: {
      type: Boolean,
      default: true
    },
    initialStartDate: {
      default: null
    },
    initialEndDate: {
      default: null
    },
    initialIsShowScheduled: {
      default: null
    },
    isReactive: {
      type: Boolean,
      default: false
    },
    isPreviewAsEmployee: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      startDate: null,
      endDate: null,
      initialLoad: false,
      debounceTimer: null,
      isShowScheduled: true,
      _keyListener: null,
      subjectsChangedListener: null,
      isSubjectModal: false,
      isSubjectLoading: false,
      editSubject: null,
      pendingSubjectTab: null,
      pendingSubjectId: null
    }
  },
  computed: {
    theUser () {
      return this.$store.state.user
    },
    schoolId () {
      return window.SCHOOL && window.SCHOOL.id ? window.SCHOOL.id : this.$route.params.school_id ? this.$route.params.school_id : null
    },
    isKindergarten () {
      return this.$store.getters.isKindergarten
    },
    user () {
      return {
        name: this.user_name,
        id: this.user_id * 1,
        user_calendar_id: this.user_calendar_id * 1
      }
    },
    isLoadingTasksForUser () {
      return this.$store.state.subjects.isLoadingTasksForUser
    },
    activeSchoolYear () {
      return this.$store.state.localActivatedSchoolYear
    },
    isStartOrEndDateChanged () {
      return this.compareDates(this.activeSchoolYear.employee_time_start, this.startDate) && this.compareDates(this.activeSchoolYear.employee_time_end, this.endDate)
    },
    dates () {
      return this.startDate ? moment(this.startDate).format('LL') + ' - ' + moment(this.endDate).format('LL') : ''
    },
    usersSorted () {
      return this.$store.getters.usersSortedOnName
    },
    isPermissionsActivatedForSchool () {
      return this.$store.getters.isPermissionsActivatedForSchool
    },
    userPermissions () {
      return this.$store.getters.userPermissions
    },
    schoolSettings () {
      return this.$store.state.schoolSettings
    },
    isPoolsDeactivated () {
      return this.schoolSettings && this.schoolSettings['deactivatePools'] ? this.schoolSettings['deactivatePools'] : false
    },
    schoolPrepFactor () {
      return parseNumericPreparationFactor(this.schoolSettings && this.schoolSettings.prepFactor)
    },
    canEditTasks () {
      return !this.isPreviewAsEmployee && this.userHasPermission('can_manage_school')
    },
    schoolYearId () {
      return this.activeSchoolYear ? this.activeSchoolYear.id : this.$store.getters.schoolYearId
    }
  },
  watch: {
    activeSchoolYear () {
      if (!this.initialStartDate) {
        this.setEndDate(this.activeSchoolYear.employee_time_end)
        this.setStartDate(this.activeSchoolYear.employee_time_start)
      }
    },
    user_id (val) {
      if (this.isReactive) {
        this.readAllSubjects()
      }
    }
  },
  methods: {
    selectNextUser () {
      const idx = this.usersSorted.findIndex(user => (user.id * 1) === (this.user_id * 1))
      const nextIdx = idx + 1
      let nextUserId = this.usersSorted[nextIdx] && this.usersSorted[nextIdx].id ? this.usersSorted[nextIdx].id : null
      if (nextUserId) {
        this.selectUser(nextUserId)
      }
    },
    selectPrevUser () {
      const idx = this.usersSorted.findIndex(user => (user.id * 1) === (this.user_id * 1))
      const nextIdx = idx - 1
      let prevUserId = this.usersSorted[nextIdx] && this.usersSorted[nextIdx].id ? this.usersSorted[nextIdx].id : null
      if (prevUserId) {
        this.selectUser(prevUserId)
      }
    },
    selectUser (id) {
      window.location = '/users/' + id + '/hours#'
    },
    setIsShowScheduled (boolean) {
      this.isShowScheduled = boolean
    },
    compareDates (firstDate, secondDate) {
      return moment(firstDate).format('YYYY-MM-DD') === moment(secondDate).format('YYYY-MM-DD')
    },
    resetDates () {
      this.setDates(this.activeSchoolYear.employee_time_start, this.activeSchoolYear.employee_time_end)
    },
    setDatesFromRange (dateRange) {
      this.setDates(dateRange.start, dateRange.end)
    },
    setDates (start, end) {
      if (start) {
        this.startDate = start
      }
      if (end) {
        this.endDate = end
      }
      this.readAllSubjects()
    },
    setStartDate (date) {
      this.startDate = date
      if (moment(date).isValid()) {
        this.readAllSubjects()
      }
    },
    setEndDate (date) {
      this.endDate = date
      if (moment(date).isValid()) {
        this.readAllSubjects()
      }
    },
    readUserTasks (isSilent = false) {
      if (!this.endDate) {
        return
      }
      const endDate = moment(this.endDate).add(1, 'days')
      // Wait for dates to be set
      setTimeout(_ => {
        let readObj = {
          userId: this.user_id,
          fromDate: this.startDate,
          toDate: endDate,
          isSilent: isSilent
        }
        this.$store.dispatch('readUserTasks', readObj)
      }, 0)
    },
    readAllSubjects (isSilent = false) {
      clearTimeout(this.debounceTimer)
      this.debounceTimer = setTimeout(_ => {
        this.readAssignedHours()
        this.readUserTasks(isSilent)
      }, 250)
    },
    readAssignedHours () {
      setTimeout(_ => {
        let schoolObj = {
          schoolId: window.SCHOOL.id,
          schoolYearId: this.activeSchoolYear.id,
          userId: this.user.id,
          fromDateTime: moment(this.startDate).format('YYYY-MM-DD') + ' 00:00',
          toDateTime: moment(this.endDate).format('YYYY-MM-DD') + ' 23:59'
        }
        this.$store.dispatch('readUsersAssignedHoursForUser', schoolObj)
        this.$store.dispatch('readUsersActualHoursForUser', schoolObj)
      }, 0)
    },
    print () {
      window.print()
    },
    userHasPermission (permission) {
      if (!this.theUser) {
        return false
      }
      if (this.isPermissionsActivatedForSchool) {
        return this.userPermissions.indexOf(permission) > -1 || this.theUser.admin
      } else {
        return true
      }
    },
    setEditSubject (task) {
      if (!task || !task.id) {
        return
      }
      this.isSubjectLoading = true
      this.isSubjectModal = true
      this.editSubject = null
      docendo.showSubject(task.id, success => {
        if (success && success.status === 'error') {
          this.closeSubjectModal()
          eventbus.toast = {text: success.message || this.$t('vue2.common.something_went_wrong'), success: false}
          return
        }
        const pendingTab = this.pendingSubjectTab
        this.pendingSubjectTab = null
        this.pendingSubjectId = null
        this.editSubject = pendingTab
          ? Object.assign({}, success, {_openTab: pendingTab})
          : Object.assign({}, success)
        this.isSubjectLoading = false
      }, failure => {
        this.closeSubjectModal()
        eventbus.toast = {text: this.$t('vue2.common.something_went_wrong'), success: false}
      })
    },
    closeSubjectModal () {
      this.editSubject = null
      this.isSubjectModal = false
      this.isSubjectLoading = false
    },
    updateSubject (updatedSubject) {
      if (!updatedSubject.schoolId) {
        updatedSubject.schoolId = this.schoolId
      }
      if (!updatedSubject.schoolYearId) {
        updatedSubject.schoolYearId = this.schoolYearId
      }
      this.$store.dispatch('updateSubject', updatedSubject)
      this.closeSubjectModal()
    },
    deleteSubject (editSubject) {
      const deletedSubject = {
        id: editSubject.id,
        school_id: editSubject.school_id,
        school_year_id: this.schoolYearId,
        schoolYearId: this.schoolYearId
      }
      this.$store.dispatch('deleteSubject', deletedSubject)
      this.closeSubjectModal()
    },
    saveAndShowTab (updatedSubject, tab) {
      this.updateSubject(updatedSubject)
      this.pendingSubjectTab = tab
      this.pendingSubjectId = updatedSubject.id
      setTimeout(() => {
        this.setEditSubject({id: this.pendingSubjectId})
      }, 500)
    }
  },
  beforeMount () {
    if (this.initialIsShowScheduled !== null) {
      this.isShowScheduled = this.initialIsShowScheduled
    }
  },
  mounted () {
    this.subjectsChangedListener = e => {
      const isSilent = !!(e && e.detail && e.detail.isUserTaskHoursUpdatedLocally)
      this.readAllSubjects(isSilent)
    }
    window.addEventListener('vueCRUDSubject', this.subjectsChangedListener, false)
    if (this.initialStartDate) {
      this.setEndDate(this.initialEndDate)
      this.setStartDate(this.initialStartDate)
      return
    }
    if (this.activeSchoolYear) {
      this.setEndDate(this.activeSchoolYear.employee_time_end)
      this.setStartDate(this.activeSchoolYear.employee_time_start)
    }
    if (document.title) {
      if (this.user_name) {
        document.title = this.user_name + ' - ' + this.$t('vue2.task_overview.hour_overview')
      }
    }
    this._keyListener = function (e) {
      const key = e.which || e.keyCode
      const metaKey = e.metaKey || e.ctrlKey
      if (metaKey && key === 39) {
        if ((e.target.tagName.toUpperCase() === 'BODY')) {
          this.selectNextUser()
        }
      }
      if (metaKey && e.shiftKey && key === 37) {
        if ((e.target.tagName.toUpperCase() === 'BODY')) {
          this.selectPrevUser()
        }
      }
    }
    document.addEventListener('keydown', this._keyListener.bind(this))
  },
  beforeDestroy () {
    document.removeEventListener('keydown', this._keyListener)
    if (this.subjectsChangedListener) {
      window.removeEventListener('vueCRUDSubject', this.subjectsChangedListener, false)
    }
  }
}
</script>

<style>
@import "../../assets/css/variables.css";

.user-tasks {
  min-height: calc(100vh - 150px);
}

.user-tasks__header {
  display: flex;
  flex-direction: column;
  margin: 1rem;
  gap: 1rem;
}

.user-tasks__header__left {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
}

.user-tasks__header__left__inner {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
}

.user-tasks__header__right {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  align-items: center;
}

@media only screen and (min-width: 768px) {
  .user-tasks__header {
    flex-direction: row;
    justify-content: space-between;
    margin: 0 0 1rem 0;
  }
  .user-tasks__header__left {
    flex-direction: row;
  }

  .user-tasks__header__left__inner {
    flex-direction: row;
    align-items: center;
    gap: 0.5rem;
  }

  .user-tasks__header__right {
    flex-direction: row;
  }
}


.user-tasks__header__right .d-link {
  color: var(--blue);
  cursor: pointer;
  margin-right: 1rem;
}

.user-tasks__header__right .d-link:hover {
  text-decoration: underline;
}

.marg-05-right {
  margin-right: 0.5rem;
}
</style>



// WEBPACK FOOTER //
// UserTasks.vue?8f54b3cc